/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect } from 'react';
import type { NavigateFunction } from 'react-router';
import type { TFunction } from 'i18next';
import { clearPersistedOnboarding } from '@features/onboarding/hooks/usePersistedOnboarding';

/**
 * Formats a Date as a zero-padded 24-hour `HH:mm` clock string.
 *
 * @param date - The date to format.
 * @returns The localized 24-hour time string.
 */
/**
 * Formats a date as a 24-hour `HH:mm` clock string using the runtime locale.
 *
 * @param date - The date to format.
 * @returns The zero-padded hour:minute string.
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * Hook exposing a live-updating `HH:mm` clock string that refreshes once per
 * minute and clears its interval on unmount.
 *
 * @returns The current time as a formatted string, updated every minute.
 */
export function useLiveClock(): string {
  const [time, setTime] = useState(() => formatTime(new Date()));

  useEffect(() => {
    const id = setInterval(() => setTime(formatTime(new Date())), 60_000);
    return () => clearInterval(id);
  }, []);

  return time;
}

type TimePeriod = 'morning' | 'afternoon' | 'evening';

const TIME_PERIOD_THRESHOLDS: [number, TimePeriod][] = [
  [18, 'evening'],
  [12, 'afternoon'],
  [0, 'morning'],
];

const GREETING_KEYS: Record<TimePeriod, Record<string, string>> = {
  morning: { named: 'workspace.greetingMorning', anonymous: 'workspace.greetingMorningNoName' },
  afternoon: { named: 'workspace.greetingAfternoon', anonymous: 'workspace.greetingAfternoonNoName' },
  evening: { named: 'workspace.greetingEvening', anonymous: 'workspace.greetingEveningNoName' },
};

/**
 * Resolves the time-of-day period (morning/afternoon/evening) for a given hour
 * using descending thresholds, defaulting to morning.
 *
 * @param hour - Hour of day in 24-hour form.
 * @returns The matching time period.
 */
function getTimePeriod(hour: number): TimePeriod {
  const match = TIME_PERIOD_THRESHOLDS.find(([threshold]) => hour >= threshold);
  return match ? match[1] : 'morning';
}

/**
 * Selects the i18n greeting key for the current time of day, choosing the named
 * or anonymous variant based on whether a user name is available.
 *
 * @param hasName - Whether a display name is present for the greeting.
 * @returns The translation key for the appropriate greeting.
 */
export function getGreetingKey(hasName: boolean): string {
  const period = getTimePeriod(new Date().getHours());
  return GREETING_KEYS[period][hasName ? 'named' : 'anonymous'];
}

/**
 * Starts a genuinely new agency onboarding attempt.
 *
 * A draft's `agencyId` is reused only while the payer on the form is
 * unchanged from the one it was created for (see `reusableAgencyId` in
 * `Payment.checkout.ts`, BUG-011/BUG-021), so a stale id left behind by an
 * abandoned attempt has to be cleared *before* this navigation -- every "add
 * agency" entry point across the workspace routes through this one call so
 * none of them can skip it.
 *
 * @param navigate - The router's navigate function.
 * @param options - Navigation options, e.g. `{ replace: true }`.
 */
export function goToNewAgency(
  navigate: NavigateFunction,
  options?: { replace?: boolean },
): void {
  clearPersistedOnboarding();
  navigate('/app/agencies?action=add', options);
}

/**
 * Derives up to two uppercase initials from the first two whitespace-separated
 * words of a name, for avatar fallbacks.
 *
 * @param name - The full name to abbreviate.
 * @returns The one- or two-letter uppercase initials.
 */
export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

interface TimeUnit { max: number; key: string; divisor: number }

const TIME_UNITS: TimeUnit[] = [
  { max: 1, key: 'workspace.lastAccess.justNow', divisor: 1 },
  { max: 60, key: 'workspace.lastAccess.minutesAgo', divisor: 1 },
  { max: 1440, key: 'workspace.lastAccess.hoursAgo', divisor: 60 },
  { max: 43200, key: 'workspace.lastAccess.daysAgo', divisor: 1440 },
  { max: Infinity, key: 'workspace.lastAccess.monthsAgo', divisor: 43200 },
];

/**
 * Renders a relative "last access" label (just now / minutes / hours / days /
 * months ago) from an ISO timestamp, returning null for missing or future times.
 *
 * @param isoTs - The last-access ISO timestamp, or null/undefined.
 * @param t - Translation function for the relative-time labels.
 * @returns The localized relative-time string, or null when unavailable.
 */
/**
 * Renders a human-friendly relative "last access" label (just now, minutes,
 * hours, days, months ago) from an ISO timestamp, returning null when the
 * timestamp is missing or lies in the future.
 *
 * @param isoTs - ISO timestamp of the last access, or null/undefined.
 * @param t - Translation function used to localize the relative label.
 * @returns The localized relative-time label, or null when unavailable.
 */
export function formatLastAccess(isoTs: string | null | undefined, t: TFunction): string | null {
  if (!isoTs) return null;
  const diffMs = Date.now() - new Date(isoTs).getTime();
  if (diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60_000);

  const unit = TIME_UNITS.find((u) => minutes < u.max)!;
  if (unit.key === 'workspace.lastAccess.justNow') return t(unit.key);
  return t(unit.key, { count: Math.floor(minutes / unit.divisor) });
}
